import React, { useState, useEffect, useCallback, useRef, useContext, useMemo } from 'react';
import styles from './ChatPage.module.css';
import axios from 'axios';
import { useAuthUser } from '../../hooks/useAuthUser';
import { UserContext } from '../../contexts/UserContext';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { MdArrowBack, MdSend, MdChatBubbleOutline, MdWorkspacePremium } from 'react-icons/md';
import { io } from 'socket.io-client';
import { buildApiBases, encodePathSegment, requestWithApiFallback } from '../../utils/apiRequest';
import { resolveSocketAuthPayload } from '../../utils/socketAuth';
import AppLoader from '../../components/AppLoader/AppLoader';
import formatDisplayName from '../../utils/formatDisplayName';
import Modal from '../../components/Modal/Modal';
import { checkSubscription } from '../../utils/checkSubscription';
import { useAuth } from '@clerk/clerk-react';
import { AppAuthContext } from '../../contexts/AppAuthContext';

const ChatPage = () => {
	const { user, isLoaded } = useAuthUser();
	const { conversationID } = useParams();
	const navigate = useNavigate();
	const { dbUser } = useContext(UserContext);
	const { getToken } = useAuth();
	const { testAuthSession } = useContext(AppAuthContext);
	const API_URL = process.env.REACT_APP_API_URL;
	const requestApi = useCallback((requestFactory) => requestWithApiFallback(API_URL, requestFactory), [API_URL]);
	const getAuthConfig = useCallback(async () => {
		let token = testAuthSession?.token || '';
		if (!token && typeof getToken === 'function') {
			try {
				token = await getToken();
			} catch (_) {
				token = '';
			}
		}

		return token
			? { headers: { Authorization: `Bearer ${token}` } }
			: {};
	}, [getToken, testAuthSession?.token]);
	const socketBaseUrl = useMemo(() => {
		const bases = buildApiBases(API_URL);
		return bases.find(base => !/\/api$/i.test(base)) || bases[0] || '';
	}, [API_URL]);

	const [conversations, setConversations] = useState(() => {
		try {
			const cached = sessionStorage.getItem('calcium_chat_cache');
			if (cached) return JSON.parse(cached);
		} catch { /* ignore */ }
		return [];
	});
	const [activeConvo, setActiveConvo] = useState(null);
	const [messages, setMessages] = useState([]);
	const [input, setInput] = useState('');
	const [loading, setLoading] = useState(true);
	const [chatError, setChatError] = useState('');
	const [sending, setSending] = useState(false);
	const messagesEndRef = useRef(null);
	const pollRef = useRef(null);
	const socketRef = useRef(null);
	const activeConvoRef = useRef(null);

	useEffect(() => {
		activeConvoRef.current = activeConvo;
	}, [activeConvo]);

	const fetchConversations = useCallback(async () => {
		if (!user?.id) return;
		setChatError('');
		setLoading(conversations.length === 0);
		try {
			const authConfig = await getAuthConfig();
			const res = await requestApi((base) => axios.get(
				`${base}/chat/conversations/${encodePathSegment(user.id)}`,
				authConfig
			));
			setConversations(res.data);
			try { sessionStorage.setItem('calcium_chat_cache', JSON.stringify(res.data)); } catch { /* ignore */ }
		} catch (err) {
			console.error('Fetch convos error:', err?.response?.data?.error || err.message);
			const status = err?.response?.status;
			if (status === 401 || status === 403) {
				setChatError('Your session expired. Please sign in again.');
			} else {
				setChatError('We could not load your conversations.');
			}
		} finally {
			setLoading(false);
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [conversations.length, getAuthConfig, requestApi, user?.id]);

	useEffect(() => {
		fetchConversations();
	}, [fetchConversations]);

	useEffect(() => {
		const handleVisibilityChange = () => {
			if (document.visibilityState === 'visible' && user?.id) {
				fetchConversations();
			}
		};
		document.addEventListener('visibilitychange', handleVisibilityChange);
		return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
	}, [fetchConversations, user?.id]);

	const openConversation = useCallback(async (convo) => {
		setActiveConvo(convo);
		setMessages(convo.messages || []);
		setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);

		try {
			const authConfig = await getAuthConfig();
			await requestApi((base) => axios.post(
				`${base}/chat/read/${encodePathSegment(convo._id)}`,
				{ userID: user.id },
				authConfig
			));
		} catch { /* silently fail */ }

		if (pollRef.current) clearInterval(pollRef.current);
		pollRef.current = setInterval(async () => {
			try {
				const authConfig = await getAuthConfig();
				const res = await requestApi((base) => axios.get(
					`${base}/chat/conversation/${encodePathSegment(convo._id)}`,
					authConfig
				));
				setMessages(res.data.messages || []);
			} catch { /* silently fail */ }
		}, 5000);
	}, [getAuthConfig, requestApi, user?.id]);

	useEffect(() => {
		if (!conversationID || conversations.length === 0) return;
		const existingConversation = conversations.find(convo => convo._id === conversationID);
		if (existingConversation) {
			openConversation(existingConversation);
		}
	}, [conversationID, conversations, openConversation]);

	useEffect(() => {
		return () => {
			if (pollRef.current) clearInterval(pollRef.current);
		};
	}, []);

	useEffect(() => {
		if (!user?.id || !socketBaseUrl) return;
		let isCancelled = false;
		let socket = null;

		const setupSocket = async () => {
			const authPayload = await resolveSocketAuthPayload({
				getToken,
				testAuthToken: testAuthSession?.token,
				userID: user.id
			});
			if (!authPayload || isCancelled) return;

			socket = io(socketBaseUrl, {
				transports: ['websocket', 'polling'],
				auth: authPayload
			});
			socketRef.current = socket;

			socket.on('chat:conversation-created', (conversation) => {
				setConversations(prev => {
					if (prev.some(item => item._id === conversation._id)) return prev;
					return [conversation, ...prev];
				});
			});

			socket.on('chat:message', ({ conversation }) => {
				if (!conversation?._id) return;
				setConversations(prev => {
					const mapped = prev.map(item => (item._id === conversation._id ? {
						...conversation,
						otherParticipant: conversation.participants?.find(participant => participant.userID !== user.id) || conversation.participants?.[0]
					} : item));
					if (mapped.some(item => item._id === conversation._id)) return mapped;
					return [{
						...conversation,
						otherParticipant: conversation.participants?.find(participant => participant.userID !== user.id) || conversation.participants?.[0]
					}, ...mapped];
				});

				if (activeConvoRef.current?._id === conversation._id) {
					setMessages(conversation.messages || []);
				}
			});

			socket.on('chat:messages-read', ({ conversationID: readConversationID }) => {
				if (!readConversationID) return;
				if (activeConvoRef.current?._id === readConversationID) {
					setMessages(prev => prev.map(message => (
						message.senderID === user.id ? { ...message, read: true } : message
					)));
				}
			});
		};

		setupSocket();

		return () => {
			isCancelled = true;
			if (socket) socket.disconnect();
			socketRef.current = null;
		};
	}, [getToken, socketBaseUrl, testAuthSession?.token, user?.id]);

	const sendMessage = async () => {
		if (!input.trim() || !activeConvo || sending) return;
		setSending(true);
		const text = input.trim();
		setInput('');

		setMessages(prev => [...prev, {
			senderID: user.id,
			senderUsername: dbUser?.username || user.firstName,
			text,
			read: false,
			createdAt: new Date().toISOString()
		}]);

		try {
			const authConfig = await getAuthConfig();
			const res = await requestApi((base) => axios.post(
				`${base}/chat/message/${encodePathSegment(activeConvo._id)}`,
				{
					senderID: user.id,
					senderUsername: dbUser?.username || user.firstName,
					text
				},
				authConfig
			));
			setMessages(res.data.messages || []);
		} catch (err) {
			console.error('Send error:', err.message);
		} finally {
			setSending(false);
			setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
		}
	};

	const getTimeDisplay = (dateStr) => {
		if (!dateStr) return '';
		const date = new Date(dateStr);
		const now = new Date();
		const diff = now - date;
		if (diff < 86400000) {
			return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
		}
		if (diff < 604800000) {
			return date.toLocaleDateString([], { weekday: 'short' });
		}
		return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
	};

	const closeModal = useCallback(() => {
		const canGoBack = typeof window !== 'undefined' && window.history.length > 1;
		if (canGoBack) {
			navigate(-1);
			return;
		}
		navigate('/');
	}, [navigate]);

	const returnToConversationList = useCallback(() => {
		setActiveConvo(null);
		if (pollRef.current) clearInterval(pollRef.current);
		fetchConversations();
		if (conversationID) {
			navigate('/chat', { replace: true });
		}
	}, [conversationID, fetchConversations, navigate]);

	if (!isLoaded) {
		return <AppLoader message='Loading messages...' />;
	}

	if (!user) {
		return (
			<div className={styles.ChatModalRoot}>
				<Modal
					onClose={closeModal}
					title='Messages'
					subtitle='Connect with your fitness crew'
					maxWidth={820}
					overlayClassName={styles.ChatOverlay}
					contentClassName={styles.ChatModalContent}
					bodyClassName={styles.ChatModalBody}
				>
					<div className={styles.ChatPage}>
						<div className={styles.EmptyState}>
							<MdChatBubbleOutline className={styles.EmptyIcon} />
							<h3>Sign in to chat</h3>
							<p>Connect with other athletes</p>
							<Link to="/login?redirectTo=/chat" className={styles.LoginLink}>Log In</Link>
						</div>
					</div>
				</Modal>
			</div>
		);
	}

	if (!checkSubscription(dbUser)) {
		return (
			<div className={styles.ChatModalRoot}>
				<Modal
					onClose={closeModal}
					title='Messages'
					subtitle='A Calcium Plus feature'
					maxWidth={520}
					overlayClassName={styles.ChatOverlay}
					contentClassName={styles.ChatModalContent}
					bodyClassName={styles.ChatModalBody}
				>
					<div className={styles.ChatPage}>
						<div className={styles.PremiumGate}>
							<div className={styles.PremiumIconWrap}>
								<MdWorkspacePremium className={styles.PremiumIcon} />
							</div>
							<h3 className={styles.PremiumTitle}>Unlock Direct Messages</h3>
							<p className={styles.PremiumDesc}>
								Chat directly with other athletes, share tips, and stay
								motivated together. Messaging is available exclusively for Calcium Plus members.
							</p>
							<Link to='/upgrade' className={styles.PremiumUpgradeBtn}>
								Upgrade to Calcium Plus
							</Link>
							<button type='button' className={styles.PremiumDismiss} onClick={closeModal}>
								Maybe later
							</button>
						</div>
					</div>
				</Modal>
			</div>
		);
	}

	const otherUser = activeConvo
		? activeConvo.otherParticipant || activeConvo.participants?.find(p => p.userID !== user.id)
		: null;

	return (
		<div className={styles.ChatModalRoot}>
			<Modal
				onClose={closeModal}
				title={activeConvo ? formatDisplayName(otherUser?.username) : 'Messages'}
				subtitle={activeConvo ? 'Direct message' : 'Connect with your fitness crew'}
				maxWidth={820}
				overlayClassName={styles.ChatOverlay}
				contentClassName={styles.ChatModalContent}
				bodyClassName={styles.ChatModalBody}
			>
				<div className={styles.ChatPage}>
					{activeConvo ? (
						<div className={styles.ConversationShell}>
							<div className={styles.ConvoHeader}>
								<button className={styles.BackBtn} onClick={returnToConversationList}>
									<MdArrowBack />
								</button>
								<Link to={`/profile/${otherUser?.userID}`} className={styles.ConvoHeaderUser}>
									{otherUser?.profileImageLink ? (
										<img src={otherUser.profileImageLink} alt="" className={styles.ConvoAvatar} />
									) : (
										<div className={styles.ConvoAvatarPlaceholder}>{otherUser?.username?.[0]?.toUpperCase()}</div>
									)}
									<div className={styles.ConvoMeta}>
										<span className={styles.ConvoUsername}>{formatDisplayName(otherUser?.username)}</span>
										<span className={styles.ConvoStatus}>Direct message</span>
									</div>
								</Link>
							</div>

							<div className={styles.MessagesContainer}>
								{messages.map((msg, i) => (
									<div
										key={i}
										className={`${styles.MessageRow} ${msg.senderID === user.id ? styles.MyMessage : styles.TheirMessage}`}
									>
										<div className={`${styles.Bubble} ${msg.senderID === user.id ? styles.MyBubble : styles.TheirBubble}`}>
											<p className={styles.BubbleText}>{msg.text}</p>
											<span className={styles.BubbleTime}>{getTimeDisplay(msg.createdAt)}</span>
										</div>
									</div>
								))}
								<div ref={messagesEndRef} />
							</div>

							<div className={styles.ChatInputArea}>
								<div className={styles.ChatInputWrapper}>
									<input
										className={styles.ChatInput}
										value={input}
										onChange={(e) => setInput(e.target.value)}
										onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
										placeholder="Type a message..."
									/>
									<button
										className={`${styles.SendBtn} ${input.trim() ? styles.SendBtnActive : ''}`}
										onClick={sendMessage}
										disabled={!input.trim() || sending}
									>
										<MdSend />
									</button>
								</div>
							</div>
						</div>
					) : (
						<div className={styles.ConversationShell}>
							{loading ? (
								<AppLoader message='Loading conversations...' />
							) : chatError ? (
								<div className={styles.EmptyState}>
									<MdChatBubbleOutline className={styles.EmptyIcon} />
									<h3>Messages unavailable</h3>
									<p>{chatError}</p>
									<button className={styles.RetryButton} onClick={fetchConversations}>Retry</button>
								</div>
							) : conversations.length === 0 ? (
								<div className={styles.EmptyState}>
									<MdChatBubbleOutline className={styles.EmptyIcon} />
									<h3>No conversations yet</h3>
									<p>Start a conversation from someone's profile</p>
								</div>
							) : (
								<div className={styles.ConvoList}>
									{conversations.map(convo => {
										const other = convo.otherParticipant;
										return (
											<button key={convo._id} className={styles.ConvoItem} onClick={() => openConversation(convo)}>
												{other?.profileImageLink ? (
													<img src={other.profileImageLink} alt="" className={styles.ConvoItemAvatar} />
												) : (
													<div className={styles.ConvoItemAvatarPlaceholder}>{other?.username?.[0]?.toUpperCase()}</div>
												)}
												<div className={styles.ConvoItemInfo}>
													<div className={styles.ConvoItemTop}>
														<span className={styles.ConvoItemUsername}>{formatDisplayName(other?.username)}</span>
														<span className={styles.ConvoItemTime}>{getTimeDisplay(convo.lastMessage?.createdAt)}</span>
													</div>
													<span className={`${styles.ConvoItemPreview} ${convo.unreadCount > 0 ? styles.Unread : ''}`}>
														{convo.lastMessage?.text || 'No messages yet'}
													</span>
												</div>
												{convo.unreadCount > 0 && (
													<span className={styles.UnreadBadge}>{convo.unreadCount}</span>
												)}
											</button>
										);
									})}
								</div>
							)}
						</div>
					)}
				</div>
			</Modal>
		</div>
	);
};

export default ChatPage;
